import { Component, Input, OnDestroy, forwardRef } from "@angular/core";
import {
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  NG_VALUE_ACCESSOR
} from "@angular/forms";
import { first, takeUntil } from "rxjs/operators";

import { BaseJsonEditorComponent } from "./base-json-editor.component";
import { CollapseAllService } from "./collapse-all.service";
import { treeAnimations } from "./json-tree-animations";

@Component({
  selector: "ta-json-array-editor",
  templateUrl: "./json-array-editor.template.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => JsonArrayEditorComponent),
      multi: true
    }
  ],
  animations: treeAnimations
})
export class JsonArrayEditorComponent extends BaseJsonEditorComponent
  implements ControlValueAccessor, OnDestroy {
  @Input()
  animate: boolean = false;

  formGroup = this.fb.group({ array: this.fb.array([]) });

  activeNewControlFormIdx?: number;
  hoveredIdx?: number;

  get formArray(): FormArray {
    return this.formGroup.get("array") as FormArray;
  }

  get indices(): number[] {
    return this.formArray.value.map((_, i) => i);
  }

  constructor(private fb: FormBuilder, collapseAllService: CollapseAllService) {
    super(collapseAllService);
  }

  removeControl(index: number): void {
    this.formArray.removeAt(index);
  }

  toggleTopNewControlForm(): void {
    this.activeNewControlFormIdx = undefined;
    super.toggleNewControlForm();
  }

  openNewControlForm(i: number): void {
    this.showNewControlForm = false;
    this.activeNewControlFormIdx = i;
  }

  submitNewControlForm(i: number, child: any): void {
    this.formArray.insert(i, this.fb.control(this.getNewChildValue(child)));
    this.showNewControlForm = false;
    this.activeNewControlFormIdx = undefined;
  }

  writeValue(arr: any[] = []): void {
    this.resetControls();
    for (let el of arr) {
      this.formArray.push(this.fb.control(el));
    }
  }

  registerOnChange(propagateChanges: any): void {
    this.formArray.valueChanges
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe(propagateChanges);
  }

  registerOnTouched(touchCb: any): void {
    this.formArray.valueChanges
      .pipe(first(), takeUntil(this.onDestroyEmitter))
      .subscribe(touchCb);
  }

  useLastChildStyling(i: number, isActuallyLast: boolean): boolean {
    return (
      isActuallyLast &&
      this.hoveredIdx !== i &&
      this.activeNewControlFormIdx !== i
    );
  }

  private resetControls(): void {
    while (this.formArray.length) {
      this.formArray.removeAt(0);
    }
  }
}
